import { useEffect, useRef, useState } from 'react';

export default function useAboutKyuOsTOCActiveSection(ids: string[], offsetTopPx = 139) {
  const [activeId, setActiveId] = useState<string>(ids[0] ?? '');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);

    const intersectionMap = new Map<string, IntersectionObserverEntry>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.id) {
            intersectionMap.set(entry.target.id, entry);
          }
        });

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          const visibleEntries = Array.from(intersectionMap.values())
            .filter((e) => e.isIntersecting)
            .sort((a, b) => {
              const ratioDiff = b.intersectionRatio - a.intersectionRatio;

              if (Math.abs(ratioDiff) < 0.1) {
                return a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top;
              }
              return ratioDiff;
            });

          const newActiveId = visibleEntries[0]?.target?.id;

          if (newActiveId && newActiveId !== activeId) {
            setActiveId(newActiveId);
          }
        }, 100);
      },
      {
        root: null,
        rootMargin: `-${offsetTopPx}px 0px -50% 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      observerRef.current?.disconnect();
    };
  }, [ids, offsetTopPx, activeId]);

  return activeId;
}
